/**
 * ThermalPrintButton — drop-in button for any screen (Iter 81).
 *
 * Props:
 *  - build(): () => receipt   // called at click time to snapshot data
 *  - label?: string           // button text (default "Print")
 *  - variant?: "primary" | "outline" | "ghost"
 *  - dataTestId?: string
 *  - className?: string
 *
 * Behavior:
 *  - On click: tries the cached printer. If none, opens the browser
 *    pair dialog. Shows toast progress + errors.
 *  - Feature-detects Web Bluetooth up front; on unsupported browsers
 *    (iOS Safari) it disables itself with a helpful tooltip.
 */
import React, { useState } from "react";
import { toast } from "sonner";
import { Printer } from "@phosphor-icons/react";
import {
  thermalPrint, pairPrinter, whyBluetoothUnavailable,
} from "../../lib/thermalPrinter";

export default function ThermalPrintButton({
  build, label = "Print", variant = "outline",
  dataTestId = "thermal-print-btn", className = "", disabled = false,
  size = "md",
}) {
  const [busy, setBusy] = useState(false);
  const unsupportedReason = whyBluetoothUnavailable();

  const onClick = async () => {
    if (busy || disabled) return;
    if (unsupportedReason) { toast.error(unsupportedReason); return; }
    let receipt;
    try { receipt = build(); }
    catch (e) { toast.error(e?.message || "Could not build receipt"); return; }
    setBusy(true);
    const t = toast.loading("Sending to printer…");
    try {
      await thermalPrint(receipt);
      toast.success("Printed", { id: t });
    } catch (e) {
      if (e?.message === "NEEDS_PAIR" || /No printer/i.test(e?.message || "")) {
        toast.message("Choose your printer", { id: t });
        try {
          await pairPrinter();
          await thermalPrint(receipt);
          toast.success("Printed", { id: t });
        } catch (e2) {
          toast.error(e2?.message || "Print failed", { id: t });
        }
      } else {
        toast.error(e?.message || "Print failed", { id: t });
      }
    } finally { setBusy(false); }
  };

  const cls = {
    primary: "bg-[#2D5A27] text-white hover:bg-[#1F3F1A] disabled:opacity-60",
    outline: "border border-[#E5E2D9] text-[#2D5A27] hover:bg-[#F2F0E9] disabled:opacity-60",
    ghost:   "text-[#2D5A27] hover:bg-[#F2F0E9] disabled:opacity-60",
  }[variant] || "";
  const sizeCls = size === "sm"
    ? "h-8 px-3 text-xs gap-1.5"
    : "h-10 px-4 text-sm gap-2";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy || disabled || !!unsupportedReason}
      title={unsupportedReason || "Print to Bluetooth thermal printer"}
      data-testid={dataTestId}
      className={`inline-flex items-center justify-center rounded-md font-medium transition-colors ${sizeCls} ${cls} ${className}`}
    >
      <Printer size={size === "sm" ? 13 : 15} weight="fill" />
      {busy ? "Printing…" : label}
    </button>
  );
}
